import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useSociete } from "../contexts/SocieteContext";
import {
  LayoutDashboard, Briefcase, ClipboardCheck,
  Upload, FileText, Users, LogOut, ChevronLeft, ChevronRight, Route,
  GraduationCap, CalendarDays, Calendar, ClipboardList, Hammer, TrendingUp, BookOpen, BookOpenCheck, FileSignature,
  UsersRound, UserCircle, MessageSquare, UserCog,
  Bell, Newspaper, Settings, KeyRound, Eye, EyeOff, Loader2, CheckCircle2,
  Receipt, Shield, Megaphone, Share2, Mail, Globe, UserSearch, Gauge,
  Sun, Moon, Wrench, ChevronDown, BarChart3, Zap, Sparkles, Pencil, Wallet, Bot, Banknote, Landmark, FolderCog,
  Building2, ChevronsUpDown, ChevronsDownUp, UserSquare, CheckSquare, Package, FolderOpen, ShieldCheck, Inbox, Network, Award, Flame, Workflow, Handshake, Activity, Search, Clock, FileSpreadsheet, Rocket, Truck, ShoppingCart, Languages, CircleUser, Home, Crown, CreditCard, Trash2, Palette, Layers, Download
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { PortalDropdown } from "./PortalDropdown";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_client-portal-252/artifacts/ymujr84m_Logo%20blitzz%20%2B%20BYB%20ok%20sans%20fond%20copie.png";

const ROLE_LABELS = { superadmin: "Master Admin", admin: "Staff Blitzz", tenant_admin: "Admin", tenant_member: "Salarié", client: "Utilisateur", eleve: "Élève", stagiaire: "Élève", formateur: "Formateur" };
const ROLE_COLORS = { superadmin: "#f7c935", admin: "#f7c935", tenant_admin: "#3b82f6", tenant_member: "#22c55e", client: "#8b5cf6", eleve: "#38bdf8", stagiaire: "#38bdf8", formateur: "#a855f7" };

// Mapping from the static FR labels used in the menu tree to i18n keys.
// When a label is not in the map we fall back to the original string.
const LABEL_I18N_MAP = {
  // Top-level
  "Tableau de bord": "nav.dashboard",
  "Dashboard": "nav.dashboard",
  "Societes": "nav.societes",
  // Mon Espace
  "Mon Espace": "nav.my_space",
  "Mes Taches": "nav.my_tasks",
  "Mon Equipement": "nav.my_equipment",
  "Mes Depenses": "nav.my_expenses",
  "Mes Documents": "nav.my_documents",
  "Messages": "nav.messages",
  "Agenda": "nav.agenda",
  // Force de vente
  "Force de vente": "nav.sales_force",
  "Clients": "nav.clients",
  "Prospects": "nav.prospects",
  "CRM": "nav.crm",
  "Facturation": "nav.billing",
  "Pilotage": "nav.piloting",
  // Blitzz services
  "Blitzz": "nav.blitzz",
  "Audits": "nav.audits",
  "Accompagnements": "nav.support",
  "Services": "nav.services",
  "Club": "nav.club",
  "Expert Strategie": "nav.expert_strategy",
  // Marketing
  "Marketing": "nav.marketing",
  "Marketing / Communication": "nav.marketing_comm",
  "Reseaux Sociaux": "nav.social_networks",
  "Mailing": "nav.mailing",
  "Google": "nav.google",
  // Compta
  "Comptabilite clients": "nav.billing_hr",
  "Social": "nav.social",
  "Comptabilite": "nav.accounting",
  // Outils
  "Outils": "nav.tools",
  "Builder": "builder.title",
  // Gestion
  "Gestion de l'activite": "nav.activity_mgmt",
  "Analytics": "nav.analytics",
  "Depenses": "nav.expenses",
  "Encaissements": "nav.incoming_payments",
  "Tresorerie": "nav.treasury",
  "Holding": "nav.holding",
  "Equipements": "nav.equipment",
  "Documents": "nav.documents",
  "Equipe": "nav.team",
  // Formation
  "Formation": "nav.formation",
  "Formation & LMS": "nav.formation_lms",
  "Pilotage Formation": "nav.training_pilot",
  "Programmes de formations": "nav.programs",
  "Modules de cours": "nav.course_modules",
  "Sessions": "nav.sessions",
  "Inscriptions": "nav.enrollments",
  "Evaluations": "nav.evaluations",
  "Documents Qualiopi": "nav.qualiopi_docs",
  "Stats BPF": "nav.bpf_stats",
  // Automatisation
  "Automatisation": "nav.automation",
  // Admin
  "Master Admin": "nav.master_admin",
  "Parametres": "nav.settings",
  "Administration": "nav.admin",
  // Bottom misc
  "Mes formations": "nav.my_formations",
  "Espace formateur": "nav.trainer_space",
  "Espace stagiaire": "nav.student_space",
  "Mes espaces formation": "nav.my_training_spaces",
  "Integrations API": "nav.integrations",
  "Marque blanche": "nav.branding",
  "Notes": "nav.notes",
  "Mode clair": "nav.light_mode",
  "Mode sombre": "nav.dark_mode",
  "Notifications": "nav.notifications",
  "Ma Fiche": "nav.ma_fiche",
  "Changer le mot de passe": "nav.change_password",
  "Mon equipe": "nav.my_team",
  "Gerer les societes": "nav.manage_societes",
  "Formations": "nav.formations",
};

function translateLabel(label, t) {
  const key = LABEL_I18N_MAP[label];
  if (!key) return label;
  const translated = t(key);
  // i18next returns the key if the translation is missing → fallback to original
  return translated && translated !== key ? translated : label;
}

// ─── Staff/Admin sidebar ─────────────────────────────
// Each link can specify a `module` key — if set, the link is only displayed
// when the active societe has `enabled_modules[<key>] === true`.
// `messages`, `agenda`, `team` and module-less links remain always-on.
const staffNav = [
  { label: null, links: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Tableau de bord", perm: null },
  ]},
  { label: "Mon Espace", links: [
    { to: "/mon-espace/taches", icon: CheckSquare, label: "Mes Taches", perm: null, module: "mon_espace" },
    { to: "/mon-espace/equipement", icon: Package, label: "Mon Equipement", perm: null, module: "mon_espace" },
    { to: "/mon-espace/depenses", icon: Wallet, label: "Mes Depenses", perm: null, module: "mon_espace" },
    { to: "/mon-espace/documents", icon: FolderOpen, label: "Mes Documents", perm: null, module: "mon_espace" },
    { to: "/mon-espace/bulletins", icon: FileText, label: "Mes Bulletins", perm: null, module: "mon_espace" },
    { to: "/messages", icon: MessageSquare, label: "Messages", perm: null }, // always-on
    { to: "/agenda", icon: CalendarDays, label: "Agenda", perm: null }, // always-on
  ]},
  { label: "Multi-Sociétés", links: [
    { to: "/admin/societes", icon: Building2, label: "Societes", perm: null },
    { to: "/admin/partenaires", icon: Handshake, label: "Partenaires", perm: null },
    { to: "/admin/holding", icon: Building2, label: "Holding", perm: "access_tresorerie", module: "gestion_activite" },
    { to: "/admin/analytics", icon: BarChart3, label: "Analytics", perm: null, module: "gestion_activite" },
  ]},
  { label: "Agents IA & Automatisation", links: [
    { to: "/admin/agent-ia", icon: Bot, label: "Agent IA", perm: null },
  ]},
  { label: "Blitzz", links: [
    { to: "/admin/audits", icon: ClipboardCheck, label: "Audits", perm: "manage_audits", module: "blitzz_accompaniment" },
    { to: "/admin/accompagnements", icon: Route, label: "Accompagnements", perm: "manage_accompagnements", module: "blitzz_accompaniment" },
    { to: "/admin/services", icon: Briefcase, label: "Services", perm: "manage_services", module: "blitzz_accompaniment" },
    { to: "/admin/club", icon: Newspaper, label: "Club", perm: null, module: "blitzz_accompaniment" },
  ]},
  { label: "Ventes & CRM", links: [
    { to: "/admin/clients", icon: Users, label: "Clients", perm: null, module: "force_de_vente" },
    { to: "/admin/prospects", icon: UserSearch, label: "Prospects", perm: null, module: "force_de_vente" },
    { to: "/admin/leads-chauds", icon: Flame, label: "Leads chauds", perm: null, module: "force_de_vente" },
    { to: "/admin/crm", icon: TrendingUp, label: "CRM", perm: null, module: "force_de_vente" },
    { to: "/admin/facturation", icon: Receipt, label: "Facturation", perm: null, module: "force_de_vente" },
    { to: "/admin/financial", icon: Gauge, label: "Pilotage", perm: "access_finances", module: "force_de_vente" },
  ]},
  { label: "Vente Augmentée IA", links: [
    { to: "/admin/commercial-panel", icon: LayoutDashboard, label: "Mon panel commercial", perm: null },
    { to: "/admin/aircall/translator", icon: Languages, label: "Traducteur d'appels", perm: null },
    { to: "/admin/objection-cookbook", icon: BookOpenCheck, label: "Cookbook objections", perm: null },
    { to: "/admin/translator/manager-stats", icon: BarChart3, label: "Stats Coach IA", perm: "manage_staff" },
  ]},
  { label: "Commandes & Stocks", links: [
    { to: "/admin/inventory", icon: Package, label: "Tableau de bord", perm: null },
    { to: "/admin/inventory/products", icon: Package, label: "Catalogue produits", perm: null },
    { to: "/admin/inventory/suppliers", icon: Truck, label: "Fournisseurs", perm: null },
    { to: "/admin/inventory/purchase-orders", icon: ShoppingCart, label: "Commandes fournisseur", perm: null },
    { to: "/admin/inventory/customer-orders", icon: ShoppingCart, label: "Commandes clients", perm: null },
    { to: "/admin/inventory/restock", icon: Bell, label: "Suggestions de réappro", perm: null },
    { to: "/admin/inventory/shopify", icon: ShoppingCart, label: "Config Shopify", perm: null, adminOnly: true },
  ]},
  { label: "Marketing / Communication", links: [
    { to: "/admin/marketing", icon: Megaphone, label: "Hub Marketing", perm: "access_marketing" },
    { to: "/admin/messagerie", icon: MessageSquare, label: "Messagerie Unifiée", perm: null, adminOnly: true },
    { to: "/admin/messagerie/stats", icon: BarChart3, label: "Stats Messagerie", perm: null, adminOnly: true },
    { to: "/admin/workflows", icon: Workflow, label: "Workflows & Automations", perm: null, adminOnly: true },
    { to: "/admin/marketing/social", icon: Share2, label: "Reseaux Sociaux", perm: "access_marketing" },
    { to: "/admin/marketing/mailing", icon: Mail, label: "Mailing", perm: "access_marketing" },
    { to: "/admin/marketing/google", icon: Globe, label: "Google", perm: "access_marketing" },
    { to: "/admin/marketing/affiliation", icon: Handshake, label: "Affiliation", perm: "access_marketing" },
  ]},
  { label: "Formation & LMS", links: [
    { to: "/admin/formation/builder", icon: Hammer, label: "Pilotage Formation", perm: "manage_formations", module: "formation" },
    { to: "/admin/formation/bibliotheque", icon: BookOpen, label: "Programmes de formations", perm: "manage_formations", module: "formation" },
    { to: "/admin/formation/modules", icon: Layers, label: "Modules de cours", perm: "manage_formations", module: "formation" },
    { to: "/admin/formation/app", icon: Download, label: "Application & hors ligne", perm: "manage_formations", module: "formation" },
    { to: "/admin/formation/sessions", icon: Calendar, label: "Sessions", perm: "manage_formations", module: "formation" },
    { to: "/admin/formation/inscriptions", icon: UsersRound, label: "Inscriptions", perm: "manage_formations", module: "formation" },
    { to: "/admin/formation/evaluations", icon: ClipboardList, label: "Evaluations", perm: "manage_formations", module: "formation" },
    { to: "/admin/formation/certifications", icon: Award, label: "Certifications RS/RNCP", perm: "manage_formations", module: "formation" },
    { to: "/admin/examens", icon: GraduationCap, label: "Examens certifiants", perm: "manage_formations" },
    { to: "/admin/formation/doc-templates", icon: FileSignature, label: "Documents Qualiopi", perm: "manage_formations", module: "formation" },
    { to: "/admin/formation/bpf", icon: BarChart3, label: "Stats BPF", perm: "manage_formations", module: "formation" },
  ]},
  { label: "Comptabilité & Finances", links: [
    { to: "/admin/depenses", icon: Wallet, label: "Depenses", perm: "manage_depenses", module: "gestion_activite" },
    { to: "/admin/encaissements", icon: Banknote, label: "Encaissements", perm: "manage_encaissements", module: "gestion_activite" },
    { to: "/admin/tresorerie", icon: Landmark, label: "Tresorerie", perm: "access_tresorerie", module: "gestion_activite" },
    { to: "/admin/previsionnel", icon: TrendingUp, label: "Previsionnel banque", perm: "access_comptabilite", module: "gestion_activite" },
    { to: "/admin/social", icon: UserCog, label: "Social — Paie", perm: "access_comptabilite", module: "gestion_activite" },
    { to: "/admin/comptabilite/generale", icon: BookOpen, label: "Comptabilite generale", perm: "access_comptabilite", module: "gestion_activite" },
    { to: "/admin/comptabilite/consolide", icon: Network, label: "Consolidation groupe", perm: "access_comptabilite", superadminOnly: true },
  ]},
  { label: "Administration", links: [
    { to: "/admin/builder", icon: Hammer, label: "Builder", perm: "access_builder" },
    { to: "/admin/outils", icon: Wrench, label: "Equipements", perm: "manage_equipements", module: "gestion_activite" },
    { to: "/admin/documents", icon: FileText, label: "Documents", perm: "manage_documents", module: "gestion_activite" },
    { to: "/admin/team", icon: UsersRound, label: "Mon équipe", perm: "manage_staff" }, // always-on (team mgmt)
    { to: "/admin/audit/permissions", icon: Shield, label: "Audit permissions", perm: "manage_staff" }, // Iter 247
    { to: "/admin/punch", icon: Clock, label: "Pointage GPS/SMS", perm: null, adminOnly: true, module: "time_tracking" },
    { to: "/admin/ppf/config", icon: FileSpreadsheet, label: "Facturation électronique", perm: null, adminOnly: true },
  ]},
];

// Bottom links (above logout)
const staffBottomLinks = [
  { to: "/admin/integrations-health", icon: Activity, label: "Hub API", perm: null, adminOnly: true },
  { to: "/admin/settings", icon: Settings, label: "Parametres", perm: null, adminOnly: true },
  { to: "/admin/aide", icon: BookOpen, label: "Centre d'aide", perm: null },
];

// ─── Client sidebar (access-controlled, grouped) ─────
const clientNavGroups = [
  { label: null, links: [
    { to: "/dashboard", icon: LayoutDashboard, label: "Tableau de bord", accessKey: null },
    { to: "/messages", icon: MessageSquare, label: "Messages", accessKey: "messages", badge: "messages" },
    { to: "/agenda", icon: CalendarDays, label: "Agenda", accessKey: "agenda" },
  ]},
  { label: "Prestations Blitzz", links: [
    { to: "/audits", icon: ClipboardCheck, label: "Audits", accessKey: "audits", badge: "audits" },
    { to: "/accompagnement", icon: Route, label: "Accompagnement", accessKey: "accompagnement", badge: "accompagnements" },
    { to: "/services", icon: Briefcase, label: "Services", accessKey: "services", badge: "services" },
    { to: "/documents", icon: FileText, label: "Documents", accessKey: "documents" },
    { to: "/club", icon: Newspaper, label: "Club", accessKey: "club" },
    { to: "/admin/agent-ia", icon: Bot, label: "Agent IA", accessKey: "expert_strategie" },
  ]},
  { label: "Formation", links: [
    { to: "/formations", icon: GraduationCap, label: "Formations", accessKey: "formations" },
  ]},
  { label: "Comptabilite Blitzz", links: [
    { to: "/social", icon: UserCog, label: "Social", accessKey: "social" },
  ]},
  { label: "Gestion activite", links: [
    { to: "/documents-gestion", icon: FileText, label: "Documents", accessKey: "documents_gestion" },
    { to: "/fiche-renseignement", icon: ClipboardList, label: "Ma Fiche", accessKey: "fiche" },
  ]},
  { label: null, links: [
    { to: "/aide", icon: BookOpen, label: "Centre d'aide", accessKey: null },
  ]},
];

export const Sidebar = ({ onNavigate }) => {
  const { user, logout, isAdmin, isStaff, axiosAuth } = useAuth();
  const { t } = useTranslation();
  const { societes, currentSociete, switchSociete, hasMutipleSocietes } = useSociete();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [showSocieteSwitcher, setShowSocieteSwitcher] = useState(false);
  const societeSwitcherRef = useRef(null);
  const [perms, setPerms] = useState({});
  const permsLoaded = Object.keys(perms).length > 0;
  const [clientAccess, setClientAccess] = useState(null);
  const [notifCounts, setNotifCounts] = useState({});
  const [showPwDialog, setShowPwDialog] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", new: "", confirm: "" });
  const [pwShow, setPwShow] = useState({ current: false, new: false, confirm: false });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState({ type: "", text: "" });
  const [sidebarLogo, setSidebarLogo] = useState("");
  const groupsStorageKey = user?.user_id ? `blitzz_sidebar_groups_${user.user_id}` : null;
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      return user?.user_id ? JSON.parse(localStorage.getItem(`blitzz_sidebar_groups_${user.user_id}`) || "{}") : {};
    } catch { return {}; }
  });
  useEffect(() => {
    if (!groupsStorageKey) return;
    try { setCollapsedGroups(JSON.parse(localStorage.getItem(groupsStorageKey) || "{}")); } catch { setCollapsedGroups({}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsStorageKey]);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const role = user?.role || "client";
  const isTenant = user?.role === "tenant_admin" || user?.role === "tenant_member";
  const tenantModules = user?.tenant?.active_modules || [];

  useEffect(() => {
    const api = axiosAuth();
    if (isTenant) {
      // Tenant users might also be Blitzz clients — load client access
      api.get("/client/my-access").then(r => setClientAccess(r.data)).catch(() => setClientAccess(null));
    } else if (user?.role === "client") {
      api.get("/client/my-access").then(r => setClientAccess(r.data)).catch(() => {});
    } else if (isStaff) {
      const headers = currentSociete?.societe_id ? { "X-Societe-Id": currentSociete.societe_id } : {};
      api.get("/auth/my-permissions", { headers }).then(r => setPerms(r.data.permissions || {})).catch(() => {});
    }
    // Load logo
    if (user?.tenant?.logo) {
      setSidebarLogo(user.tenant.logo);
    } else if (isStaff) {
      api.get("/admin/settings/logo").then(r => setSidebarLogo(r.data?.company_logo || "")).catch(() => {});
    } else {
      // Clients: /admin/* is forbidden, fall back to the public endpoint.
      api.get("/public/logo").then(r => setSidebarLogo(r.data?.company_logo || "")).catch(() => {});
    }
    // Load notification counts only if user is authenticated
    if (user) {
      const refetch = () => api.get("/notifications/counts").then(r => setNotifCounts(r.data)).catch(() => {});
      refetch();
      const interval = setInterval(refetch, 15000);
      window.addEventListener("blitzz:notifs-updated", refetch);
      return () => {
        clearInterval(interval);
        window.removeEventListener("blitzz:notifs-updated", refetch);
      };
    }
  }, [isStaff, user?.role, currentSociete?.societe_id]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleChangePassword = async () => {
    setPwMsg({ type: "", text: "" });
    if (!pwForm.current || !pwForm.new) { setPwMsg({ type: "error", text: t("password.required") }); return; }
    if (pwForm.new.length < 6) { setPwMsg({ type: "error", text: t("password.too_short") }); return; }
    if (pwForm.new !== pwForm.confirm) { setPwMsg({ type: "error", text: t("password.mismatch") }); return; }
    setPwLoading(true);
    try {
      const api = axiosAuth();
      await api.put("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.new });
      setPwMsg({ type: "success", text: t("password.success") });
      setPwForm({ current: "", new: "", confirm: "" });
      setTimeout(() => { setShowPwDialog(false); setPwMsg({ type: "", text: "" }); }, 1500);
    } catch (e) {
      setPwMsg({ type: "error", text: e.response?.data?.detail || t("password.error") });
    }
    setPwLoading(false);
  };

  // ─── Build nav groups ─────────────────────────────
  let mainGroups = [];
  let bottomLinks = [];

  // Module-to-nav mapping for tenant sidebar (only tenant-level modules)
  // Iter 255 — groupes alignés sur la taxonomie unifiée (MODULE_CATEGORIES_ORDER backend)
  const TENANT_MODULE_NAV = {
    // Mon Espace
    espace_taches: { to: "/mon-espace/taches", icon: CheckSquare, label: "Mes Taches", group: "Mon Espace", module: "mon_espace" },
    espace_equipement: { to: "/mon-espace/equipement", icon: Package, label: "Mon Equipement", group: "Mon Espace", module: "mon_espace" },
    espace_depenses: { to: "/mon-espace/depenses", icon: Wallet, label: "Mes Depenses", group: "Mon Espace", module: "mon_espace" },
    espace_documents: { to: "/mon-espace/documents", icon: FolderOpen, label: "Mes Documents", group: "Mon Espace", module: "mon_espace" },
    espace_bulletins: { to: "/mon-espace/bulletins", icon: FileText, label: "Mes Bulletins", group: "Mon Espace", module: "mon_espace" },
    messages: { to: "/messages", icon: MessageSquare, label: "Messages", badge: "messages", group: "Mon Espace" }, // always-on
    agenda: { to: "/agenda", icon: CalendarDays, label: "Agenda", group: "Mon Espace" }, // always-on
    // Ventes & CRM
    clients: { to: "/admin/clients", icon: Users, label: "Clients", group: "Ventes & CRM", module: "force_de_vente", requirePerm: "clients.view" },
    prospects: { to: "/admin/prospects", icon: UserSearch, label: "Prospects", group: "Ventes & CRM", module: "force_de_vente", requirePerm: "clients.view" },
    crm: { to: "/admin/crm", icon: TrendingUp, label: "CRM", group: "Ventes & CRM", module: "force_de_vente", requirePerm: "crm.view" },
    facturation: { to: "/admin/facturation", icon: Receipt, label: "Facturation", group: "Ventes & CRM", module: "force_de_vente", requirePerm: "facturation.view" },
    pilotage: { to: "/admin/pilotage", icon: BarChart3, label: "Pilotage", group: "Ventes & CRM", module: "force_de_vente", requirePerm: "crm.view" },
    yousign: { to: "/admin/integrations-health", icon: ShieldCheck, label: "Signature Yousign", group: "Ventes & CRM", adminOnly: true },
    intel_agent: { to: "/admin/integrations-health", icon: Search, label: "Enquêteur IA", group: "Ventes & CRM", adminOnly: true },
    // Vente Augmentée IA (Iter 248-253)
    aircall_translator: { to: "/admin/aircall/translator", icon: Languages, label: "Traducteur d'appels", group: "Vente Augmentée IA", requirePerm: "translator.use" },
    commercial_panel: { to: "/admin/commercial-panel", icon: LayoutDashboard, label: "Mon panel commercial", group: "Vente Augmentée IA", requirePerm: "panel.use" },
    objection_cookbook: { to: "/admin/objection-cookbook", icon: BookOpenCheck, label: "Cookbook objections", group: "Vente Augmentée IA", requirePerm: "cookbook.view" },
    coach_stats: { to: "/admin/translator/manager-stats", icon: BarChart3, label: "Stats Coach IA", group: "Vente Augmentée IA", adminOnly: true },
    weekly_digest: { to: "/admin/translator/manager-stats", icon: BarChart3, label: "Stats Coach IA", group: "Vente Augmentée IA", adminOnly: true },
    // Commandes & Stocks (Iter 245)
    inventory: [
      { to: "/admin/inventory", icon: Package, label: "Tableau de bord", group: "Commandes & Stocks", requirePerm: "inventory.view" },
      { to: "/admin/inventory/products", icon: Package, label: "Catalogue produits", group: "Commandes & Stocks", requirePerm: "inventory.view" },
      { to: "/admin/inventory/suppliers", icon: Truck, label: "Fournisseurs", group: "Commandes & Stocks", requirePerm: "inventory.view" },
      { to: "/admin/inventory/purchase-orders", icon: ShoppingCart, label: "Commandes fournisseur", group: "Commandes & Stocks", requirePerm: "inventory.view" },
      { to: "/admin/inventory/customer-orders", icon: ShoppingCart, label: "Commandes clients", group: "Commandes & Stocks", requirePerm: "inventory.view" },
      { to: "/admin/inventory/restock", icon: Bell, label: "Suggestions de réappro", group: "Commandes & Stocks", requirePerm: "inventory.view" },
      { to: "/admin/inventory/shopify", icon: ShoppingCart, label: "Config Shopify", group: "Commandes & Stocks", adminOnly: true },
    ],
    // Marketing
    marketing_social: { to: "/admin/marketing/social", icon: Share2, label: "Reseaux Sociaux", group: "Marketing / Communication", requirePerm: "marketing.view" },
    marketing_mailing: { to: "/admin/marketing/mailing", icon: Mail, label: "Mailing", group: "Marketing / Communication", requirePerm: "marketing.view" },
    marketing_google: { to: "/admin/marketing/google", icon: Globe, label: "Google", group: "Marketing / Communication", requirePerm: "marketing.view" },
    workflows: [
      { to: "/admin/workflows", icon: Workflow, label: "Workflows & Automations", group: "Marketing / Communication", requirePerm: "marketing.write" },
    ],
    messaging_meta: [
      { to: "/admin/messagerie", icon: MessageSquare, label: "Messagerie Unifiée", group: "Marketing / Communication", requirePerm: "meta.inbox.view" },
      { to: "/admin/messagerie/stats", icon: BarChart3, label: "Stats Messagerie", group: "Marketing / Communication", requirePerm: "meta.inbox.view" },
    ],
    briefs_hebdo: { to: "/admin/marketing", icon: Megaphone, label: "Hub Marketing", group: "Marketing / Communication", requirePerm: "marketing.view" },
    affiliate: { to: "/admin/marketing/affiliation", icon: Handshake, label: "Affiliation", group: "Marketing / Communication", requirePerm: "marketing.view" },
    // Formation
    qualiopi: { to: "/admin/qualiopi", icon: Shield, label: "Qualiopi", group: "Formation & LMS", module: "formation", requirePerm: "formation.view" },
    formations: [
      { to: "/admin/formation/builder", icon: Hammer, label: "Pilotage Formation", group: "Formation & LMS", module: "formation", requirePerm: "formation.view" },
      { to: "/admin/formation/modules", icon: Layers, label: "Modules de cours", group: "Formation & LMS", module: "formation", requirePerm: "formation.view" },
      { to: "/admin/formation/app", icon: Download, label: "Application & hors ligne", group: "Formation & LMS", module: "formation", requirePerm: "formation.view" },
    ],
    formation_bibliotheque: [
      { to: "/admin/formation/bibliotheque", icon: BookOpen, label: "Programmes de formations", group: "Formation & LMS", module: "formation", requirePerm: "formation.view" },
    ],
    formation_lms: [
      { to: "/admin/formation/sessions", icon: Calendar, label: "Sessions", group: "Formation & LMS", module: "formation", requirePerm: "formation.view" },
    ],
    formation_inscriptions: [
      { to: "/admin/formation/inscriptions", icon: UsersRound, label: "Inscriptions", group: "Formation & LMS", module: "formation", requirePerm: "formation.view" },
    ],
    formation_bpf: [
      { to: "/admin/formation/bpf", icon: BarChart3, label: "Stats BPF", group: "Formation & LMS", module: "formation", requirePerm: "formation.view" },
    ],
    formation_certifications: [
      { to: "/admin/formation/certifications", icon: Award, label: "Certifications RS/RNCP", group: "Formation & LMS", module: "formation", requirePerm: "formation.view" },
    ],
    certifications: [
      { to: "/admin/examens", icon: GraduationCap, label: "Examens certifiants", group: "Formation & LMS", requirePerm: "formation.view" },
    ],
    formation_conformite: [
      { to: "/admin/formation/builder", icon: ShieldCheck, label: "Conformite (via pilotage)", group: "Formation & LMS", module: "formation", requirePerm: "formation.view" },
    ],
    formation_evaluations: [
      { to: "/admin/formation/evaluations", icon: ClipboardList, label: "Evaluations", group: "Formation & LMS", module: "formation", requirePerm: "formation.view" },
      { to: "/admin/formation/doc-templates", icon: FileSignature, label: "Documents Qualiopi", group: "Formation & LMS", module: "formation", requirePerm: "formation.view" },
    ],
    formation_catalogue: { to: "/admin/catalogue-leads", icon: Globe, label: "Catalogue (leads)", group: "Formation & LMS", module: "formation", requirePerm: "formation.view" },
    welcome_stagiaire: { to: "/admin/settings?tab=api&section=welcome", icon: Mail, label: "Welcome Stagiaire J-7", group: "Formation & LMS", adminOnly: true },
    // Comptabilité & Finances
    depenses: { to: "/admin/depenses", icon: Wallet, label: "Depenses", group: "Comptabilité & Finances", module: "gestion_activite", requirePerm: "depenses.view" },
    encaissements: { to: "/admin/encaissements", icon: Banknote, label: "Encaissements", group: "Comptabilité & Finances", module: "gestion_activite", requirePerm: "encaissements.view" },
    tresorerie: { to: "/admin/tresorerie", icon: Landmark, label: "Tresorerie", group: "Comptabilité & Finances", module: "gestion_activite", requirePerm: "encaissements.view" },
    comptabilite: [
      { to: "/admin/comptabilite/generale", icon: BookOpen, label: "Comptabilite generale", group: "Comptabilité & Finances", module: "gestion_activite", requirePerm: "comptabilite.view" },
      { to: "/admin/previsionnel", icon: TrendingUp, label: "Prévisionnel banque", group: "Comptabilité & Finances", module: "gestion_activite", requirePerm: "comptabilite.view" },
      { to: "/admin/comptabilite/consolide", icon: Network, label: "Consolidation groupe", group: "Comptabilité & Finances", requirePerm: "comptabilite.view", requireTenantModule: "societes" },
    ],
    social: { to: "/admin/social", icon: UserCog, label: "Social", group: "Comptabilité & Finances", module: "gestion_activite", requirePerm: "comptabilite.view" },
    // Multi-Sociétés
    holding: { to: "/admin/holding", icon: Building2, label: "Holding", group: "Multi-Sociétés", module: "gestion_activite", requirePerm: "holding.view" },
    societes: { to: "/admin/societes", icon: Building2, label: "Societes", group: "Multi-Sociétés", adminOnly: true },
    partenaires: { to: "/admin/partenaires", icon: Handshake, label: "Partenaires", group: "Multi-Sociétés", adminOnly: true },
    analytics: { to: "/admin/analytics", icon: BarChart3, label: "Analytics", group: "Multi-Sociétés", module: "gestion_activite", requirePerm: "holding.analytics" },
    // Agents IA & Automatisation
    agent_ia: { to: "/admin/agent-ia", icon: Bot, label: "Agent IA", group: "Agents IA & Automatisation", requirePerm: "ai.agent.use" },
    // Administration
    builder: { to: "/admin/builder", icon: Hammer, label: "Builder", group: "Administration", adminOnly: true },
    outils: { to: "/admin/outils", icon: Wrench, label: "Equipements", group: "Administration", module: "gestion_activite", adminOnly: true },
    documents: { to: "/admin/documents", icon: FileText, label: "Documents", group: "Administration", module: "gestion_activite", requirePerm: "documents.view" },
    equipe: { to: "/tenant/team", icon: UsersRound, label: "Mon équipe", group: "Administration", adminOnly: true },
    autorisations_societes: { to: "/tenant/team/autorisations-societes", icon: UsersRound, label: "Autorisations par société", group: "Administration", adminOnly: true },
    time_tracking: { to: "/admin/punch", icon: Clock, label: "Pointage GPS/SMS", group: "Administration", adminOnly: true },
  };

  // Client-level nav (Prestations Blitzz + Comptabilite) — shown when tenant is also a Blitzz client
  const CLIENT_BLITZZ_NAV = [
    { label: "Prestations Blitzz", isBlitzz: true, links: [
      { to: "/audits", icon: ClipboardCheck, label: "Audits", accessKey: "audits" },
      { to: "/accompagnement", icon: Route, label: "Accompagnement", accessKey: "accompagnement" },
      { to: "/services", icon: Briefcase, label: "Services", accessKey: "services" },
      { to: "/documents", icon: FileText, label: "Documents", accessKey: "documents" },
      { to: "/club", icon: Newspaper, label: "Club", accessKey: "club" },
      { to: "/admin/agent-ia", icon: Bot, label: "Expert Strategie", accessKey: "expert_strategie" },
    ]},
    { label: "Formation", isBlitzz: true, links: [
      { to: "/formations", icon: GraduationCap, label: "Formations", accessKey: "formations" },
    ]},
    { label: "Comptabilite Blitzz", isBlitzz: true, links: [
      { to: "/social", icon: UserCog, label: "Social", accessKey: "social" },
    ]},
  ];

  if (isTenant) {
    // Build tenant sidebar from active modules — grouped like admin
    const groupMap = {};
    const groupOrder = ["Mon Espace", "Multi-Sociétés", "Agents IA & Automatisation", "Ventes & CRM", "Vente Augmentée IA", "Commandes & Stocks", "Marketing / Communication", "Formation & LMS", "Comptabilité & Finances", "Administration"];

    for (const mod of tenantModules) {
      const nav = TENANT_MODULE_NAV[mod];
      if (!nav) continue;
      // Skip legacy umbrella module: its children render individually
      if (mod === "espace_personnel") continue;
      const navs = Array.isArray(nav) ? nav : [nav];
      for (const n of navs) {
        // Iter 245.4 — Respect adminOnly flag (hide from tenant_member)
        if (n.adminOnly && user?.role === "tenant_member") continue;
        // Iter 296 — Some links need another tenant module (ex: consolidation → societes)
        if (n.requireTenantModule && !tenantModules.includes(n.requireTenantModule)) continue;
        // Iter 246.4 — Respect fine-grained module_permissions for tenant_member.
        // tenant_admin and superadmin always pass through (no gating).
        if (n.requirePerm && user?.role === "tenant_member") {
          const perms = user?.module_permissions || {};
          if (!perms[n.requirePerm]) continue;
        }
        const grp = n.group || "Administration";
        if (!groupMap[grp]) groupMap[grp] = [];
        // Iter 255 — dedupe (ex: coach_stats + weekly_digest pointent la même page)
        if (groupMap[grp].some(l => l.to === n.to)) continue;
        groupMap[grp].push({ ...n, perm: null });
      }
    }

    // Always include AI tools for tenants (included by default, gated by perms for members)
    const memberPerms = user?.module_permissions || {};
    const isMemberRole = user?.role === "tenant_member";
    if (!groupMap["Agents IA & Automatisation"]) groupMap["Agents IA & Automatisation"] = [];
    for (const aiLink of [
      { to: "/admin/agent-ia", icon: Bot, label: "Agent IA", perm: null, requirePerm: "ai.agent.use" },
    ]) {
      if (isMemberRole && !memberPerms[aiLink.requirePerm]) continue;
      if (!groupMap["Agents IA & Automatisation"].some(l => l.to === aiLink.to)) {
        groupMap["Agents IA & Automatisation"].push(aiLink);
      }
    }
    // Holding always available when tresorerie is active (admin-only for members)
    if (tenantModules.includes("tresorerie") && !isMemberRole) {
      if (!groupMap["Multi-Sociétés"]) groupMap["Multi-Sociétés"] = [];
      if (!groupMap["Multi-Sociétés"].some(l => l.to === "/admin/holding")) {
        groupMap["Multi-Sociétés"].push({ to: "/admin/holding", icon: Building2, label: "Holding", perm: null });
      }
    }

    // Iter 320 — Partenaires (panel partenaire) pour les tenant admins
    if (user?.role === "tenant_admin") {
      if (!groupMap["Multi-Sociétés"]) groupMap["Multi-Sociétés"] = [];
      if (!groupMap["Multi-Sociétés"].some(l => l.to === "/admin/partenaires")) {
        groupMap["Multi-Sociétés"].push({ to: "/admin/partenaires", icon: Handshake, label: "Partenaires", perm: null });
      }
    }

    // Messagerie Unifiée (Meta) always available for tenant admins
    if (user?.role === "tenant_admin") {
      if (!groupMap["Marketing / Communication"]) groupMap["Marketing / Communication"] = [];
      // Insert at top of Marketing if not already there
      if (!groupMap["Marketing / Communication"].some(l => l.to === "/admin/messagerie")) {
        groupMap["Marketing / Communication"].unshift({ to: "/admin/messagerie", icon: MessageSquare, label: "Messagerie Unifiée", perm: null });
      }
      // Iter 246.6 — "Mon équipe" always-on for tenant_admin (independent of `equipe` module).
      // This is the entry point to manage team members and their permissions.
      if (!groupMap["Administration"]) groupMap["Administration"] = [];
      if (!groupMap["Administration"].some(l => l.to === "/tenant/team")) {
        groupMap["Administration"].push({ to: "/tenant/team", icon: UsersRound, label: "Mon équipe", perm: null });
        groupMap["Administration"].push({ to: "/tenant/team/autorisations-societes", icon: UsersRound, label: "Autorisations par société", perm: null });
      }
      // Iter 259 — Page dédiée "Gestion des accès" (matrice membres × modules)
      if (!groupMap["Administration"].some(l => l.to === "/tenant/team/access")) {
        groupMap["Administration"].push({ to: "/tenant/team/access", icon: KeyRound, label: "Gestion des accès", perm: null });
      }
      // Iter 247 — Audit permissions for tenant_admin
      if (!groupMap["Administration"].some(l => l.to === "/admin/audit/permissions")) {
        groupMap["Administration"].push({ to: "/admin/audit/permissions", icon: Shield, label: "Audit permissions", perm: null });
      }
    }

    // "Mes pointages" (Iter 243) — only for tenant_member (employés salariés).
    // tenant_admin already has the admin "Pointage GPS/SMS" page in Gestion de l'activité.
    if (user?.role === "tenant_member") {
      if (!groupMap["Mon Espace"]) groupMap["Mon Espace"] = [];
      if (!groupMap["Mon Espace"].some(l => l.to === "/mon-espace/pointages")) {
        groupMap["Mon Espace"].push({ to: "/mon-espace/pointages", icon: Clock, label: "Mes pointages", perm: null });
      }
    }

    mainGroups = [
      { label: null, links: [{ to: "/dashboard", icon: LayoutDashboard, label: "Tableau de bord", perm: null }] },
      ...groupOrder.filter(g => groupMap[g]?.length).map(g => ({ label: g, links: groupMap[g] })),
    ];

    // If tenant user also has Blitzz client access, append client modules
    if (clientAccess) {
      const hasAnyBlitzzAccess = ["audits", "accompagnement", "services", "formations", "documents", "club", "expert_strategie"].some(k => clientAccess[k]);
      const hasAnyComptaAccess = ["social"].some(k => clientAccess[k]);

      if (hasAnyBlitzzAccess || hasAnyComptaAccess) {
        const blitzzGroups = CLIENT_BLITZZ_NAV
          .map(g => ({
            ...g,
            links: g.links.filter(l => l.accessKey === null || clientAccess?.[l.accessKey]),
          }))
          .filter(g => g.links.length > 0);
        mainGroups = [...mainGroups, ...blitzzGroups];
      }
    }

    bottomLinks = [
      { to: "/admin/integrations-health", icon: Activity, label: "Hub API", perm: null },
      { to: "/tenant/team", icon: UsersRound, label: "Mon equipe", perm: null },
      { to: "/tenant/settings", icon: Settings, label: "Parametres", perm: null },
      { to: "/admin/aide", icon: BookOpen, label: "Centre d'aide", perm: null },
    ];
    if (user?.role === "tenant_member") {
      // Iter 272 — separation stricte : le salarié ne voit ni Hub API, ni
      // Paramètres, ni Mon équipe (management réservé au tenant_admin).
      bottomLinks = bottomLinks.filter(l => l.to === "/admin/aide");
    }
  } else if (role === "client" || role === "eleve" || role === "stagiaire") {
    // Iter 283 — couche User unifiée : un élève est un User avec le rôle
    // secondaire "eleve" (l'espace formation s'ajoute via formationSpaces).
    mainGroups = clientNavGroups.map(g => ({
      ...g,
      links: g.links.filter(l => l.accessKey === null || clientAccess?.[l.accessKey]),
    })).filter(g => g.links.length > 0);
    bottomLinks = [];
  } else if (role === "superadmin") {
    mainGroups = staffNav.map(g => ({ ...g, links: g.links.filter(() => true) })).filter(g => g.links.length > 0);
    bottomLinks = staffBottomLinks;
  } else if (role === "admin") {
    // Iter 351 — membre d'équipe : menu filtré par les autorisations effectives de la société active (poste + surcharges)
    mainGroups = staffNav.map(g => ({
      ...g,
      links: g.links.filter(l => !l.superadminOnly && (l.perm === null || l.perm === undefined || perms[l.perm] !== false && (perms[l.perm] || !permsLoaded))),
    })).filter(g => g.links.length > 0);
    bottomLinks = staffBottomLinks.filter(l => !l.superadminOnly && (l.perm === null || l.perm === undefined || perms[l.perm] || !permsLoaded));
  } else {
    // Coach, Sous-traitant, Comptable: filter by perms
    mainGroups = staffNav.map(g => ({
      ...g,
      links: g.links.filter(l => (l.perm === null || perms[l.perm]) && !l.superadminOnly),
    })).filter(g => g.links.length > 0);
    bottomLinks = staffBottomLinks.filter(l => l.perm === null || perms[l.perm]).filter(l => !l.adminOnly);
  }

  // ─── Lien Configuration (fonctions globales + par utilisateur) sous Hub API ──
  if (["admin", "superadmin", "tenant_admin"].includes(role)) {
    const hubIdx = bottomLinks.findIndex((l) => l.to === "/admin/integrations-health");
    const cfgLink = { to: "/admin/configuration", icon: Wrench, label: "Configuration", perm: null };
    bottomLinks = [...bottomLinks.slice(0, hubIdx + 1), cfgLink, ...bottomLinks.slice(hubIdx + 1)];
  }

  // ─── Filter by per-societe enabled modules ──────────────────
  // ─── Module gating per société ─────────────────────────
  // A tenant module that declares a `module` key is hidden when:
  //   - we have an active société AND
  //   - that société's `enabled_modules[module]` is **explicitly** false.
  //
  // Tolerant logic (fix iter_188): missing/undefined/empty enabled_modules
  // means "no opt-out configured" → show the link. This avoids the trap
  // where activating a module at tenant level had no visible effect because
  // the tenant had no société (or a default-empty enabled_modules dict).
  // Always-on links (no `module:` key) remain visible: dashboard, societes,
  // messages, agenda, equipe, builder, marketing, etc.
  const enabledModules = currentSociete?.enabled_modules || null;
  const isModuleEnabled = (link) => {
    if (!link.module) return true;
    // Iter 275 — l'espace PERSONNEL n'est pas scopé société : toujours visible
    if (link.module === "mon_espace") return true;
    if (!enabledModules) return true;            // no société context → permissive
    if (Object.keys(enabledModules).length === 0) return true; // empty config → permissive
    return enabledModules[link.module] !== false; // hide ONLY on explicit false
  };
  mainGroups = mainGroups.map(g => ({
    ...g,
    links: g.links.filter(isModuleEnabled),
  })).filter(g => g.links.length > 0);

  // Iter 313 — lien « Groupe » (consolidation holding) visible uniquement depuis une société holding
  if (currentSociete?.type === "holding") {
    const idx = mainGroups.findIndex(g => g.title === "Multi-Sociétés" || (g.links || []).some(l => l.to === "/admin/societes" || l.to === "/admin/holding"));
    const groupLink = { to: "/admin/groupe", icon: Network, label: "Groupe (consolidation)", perm: null };
    if (idx >= 0) {
      if (!mainGroups[idx].links.some(l => l.to === "/admin/groupe")) mainGroups[idx].links.push(groupLink);
    } else if (mainGroups.length) {
      mainGroups[mainGroups.length - 1].links.push(groupLink);
    }
  }

  // Cross-cutting: if the user holds secondary roles (formateur / stagiaire), surface the dedicated spaces.
  const userRoles = Array.isArray(user?.roles) ? user.roles : [];
  const hasFormateur = userRoles.includes("formateur") || user?.role === "formateur";
  const hasStagiaire = userRoles.includes("stagiaire") || userRoles.includes("eleve") || user?.role === "stagiaire" || user?.role === "eleve";
  const formationSpaces = [];
  if (hasFormateur) formationSpaces.push({ to: "/formateur/sessions", icon: GraduationCap, label: "Espace formateur", perm: null });
  if (hasStagiaire) formationSpaces.push({ to: "/mes-formations", icon: BookOpen, label: "Espace stagiaire", perm: null });
  if (hasStagiaire) formationSpaces.push({ to: "/mes-parcours-certifiants", icon: Award, label: "Mes parcours certifiants", perm: null });
  if (formationSpaces.length && role !== "eleve" && role !== "stagiaire") {
    // Insert at the top just after the dashboard group
    mainGroups = [
      ...(mainGroups.slice(0, 1)),
      { label: "Mes espaces formation", links: formationSpaces },
      ...(mainGroups.slice(1)),
    ];
  }

  const getBadge = (link) => {
    if (!link.badge) return 0;
    return notifCounts[link.badge] || 0;
  };

  // ─── Iter 271/272 — Vraie séparation Panel Admin / Panel User (« Mon Espace ») ─
  // Deux univers distincts :
  //   - Panel Admin  : back-office de gestion (superadmin → multi-tenant,
  //     admin/tenant_admin → gestion des users/clients de sa plateforme).
  //   - Mon Espace   : panel UTILISATEUR, identique au portail client
  //     (espace personnel + prestations Blitzz + formations + compta client).
  // Les admins gardent un accès aux deux via le commutateur ; les users sans
  // droits admin vivent dans Mon Espace.
  const ESPACE_GROUP_LABELS = ["Mon Espace", "Mes espaces formation"];
  const ESPACE_ROUTE_PREFIXES = [
    "/mon-espace", "/messages", "/agenda", "/formateur", "/mes-formations", "/mes-parcours-certifiants",
    // Portail client (panel user)
    "/audits", "/accompagnement", "/services", "/club", "/formations", "/social",
    "/documents", "/documents-gestion", "/fiche-renseignement", "/mes-factures", "/aide",
  ];
  const isEspaceGroup = (g) => ESPACE_GROUP_LABELS.includes(g.label) || g.isBlitzz === true;
  // Rôles "portail seul" : pas de split admin/espace ni de commutateur
  const isPortalOnly = role === "client" || role === "eleve" || role === "stagiaire";
  const espaceMode = !isPortalOnly && ESPACE_ROUTE_PREFIXES.some(p => location.pathname === p || location.pathname.startsWith(p + "/"));
  const espaceGroups = mainGroups.filter(isEspaceGroup);
  const adminGroups = mainGroups.filter(g => !isEspaceGroup(g));
  const hasAdminAccess = !isPortalOnly && adminGroups.some(g => g.label !== null);
  const hasEspaceAccess = espaceGroups.length > 0;
  const showSpaceSwitch = !isPortalOnly && hasAdminAccess && hasEspaceAccess;
  if (!isPortalOnly) {
    if (espaceMode) {
      mainGroups = [
        { label: null, links: [{ to: "/mon-espace", icon: Home, label: "Accueil", perm: null, end: true }] },
        ...espaceGroups,
      ];
      // Centre d'aide reste accessible dans le panel user via la route client /aide
      bottomLinks = bottomLinks.filter(l => l.to === "/admin/aide").map(l => ({ ...l, to: "/aide" }));
    } else {
      mainGroups = adminGroups;
    }
  }
  // Salarié sans droits admin qui atterrit sur /dashboard → renvoi vers Mon Espace
  const shouldRedirectToEspace = !isPortalOnly && !hasAdminAccess && hasEspaceAccess && !espaceMode && location.pathname === "/dashboard";
  // Élève pur sur /dashboard → renvoi vers son espace formation
  const shouldRedirectStudent = (role === "eleve" || role === "stagiaire") && location.pathname === "/dashboard";
  useEffect(() => {
    if (shouldRedirectToEspace) navigate("/mon-espace", { replace: true });
    else if (shouldRedirectStudent) navigate("/mes-formations", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRedirectToEspace, shouldRedirectStudent]);

  const toggleGroup = (label) => {
    setCollapsedGroups(prev => {
      const next = { ...prev, [label]: !prev[label] };
      if (groupsStorageKey) { try { localStorage.setItem(groupsStorageKey, JSON.stringify(next)); } catch {} }
      return next;
    });
  };

  // Dédoublonnage des liens par groupe (évite les warnings React de clés dupliquées
  // quand un même lien est poussé par deux modules, ex: /admin/formation/modules)
  mainGroups = mainGroups.map(g => {
    const seen = new Set();
    return { ...g, links: (g.links || []).filter(l => (seen.has(l.to) ? false : (seen.add(l.to), true))) };
  });

  const groupLabels = mainGroups.filter(g => g.label).map(g => g.label);
  const allGroupsCollapsed = groupLabels.length > 0 && groupLabels.every(l => collapsedGroups[l]);
  const toggleAllGroups = () => {
    const next = { ...collapsedGroups };
    groupLabels.forEach(l => { next[l] = !allGroupsCollapsed; });
    setCollapsedGroups(next);
    if (groupsStorageKey) { try { localStorage.setItem(groupsStorageKey, JSON.stringify(next)); } catch {} }
  };

  const NavItem = ({ link }) => {
    const badge = getBadge(link);
    return (
      <NavLink
        key={link.to}
        to={link.to}
        end={link.end === true}
        onClick={onNavigate}
        data-testid={`nav-${link.to.replace(/[/?=&]/g, "-").replace(/^-/, "")}`}
        className={({ isActive }) => {
          const [path, query] = link.to.split("?");
          const active = query
            ? location.pathname === path && location.search === `?${query}`
            : link.noQuery
              ? location.pathname === path && !location.search
              : isActive;
          return `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 relative ${
            active ? "bg-[#f7c935]/8 text-[#f7c935] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-full before:bg-[#f7c935]" : "text-[#888194] hover:text-white hover:bg-white/[0.04]"
          }`;
        }}
      >
        <link.icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.5} />
        {!collapsed && <span className="flex-1">{translateLabel(link.label, t)}</span>}
        {!collapsed && badge > 0 && (
          <span className="min-w-[20px] h-5 flex items-center justify-center rounded-full text-[10px] font-bold bg-red-500 text-white px-1" data-testid={`badge-${link.badge}`}>
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </NavLink>
    );
  };

  const totalNotifs = notifCounts.total || 0;

  return (
    <aside
      data-testid="sidebar"
      className={`${collapsed ? "w-20" : "w-64"} h-screen max-md:h-full max-md:w-full flex flex-col border-r border-white/[0.08] transition-[width] duration-200 overflow-y-auto overflow-x-hidden flex-shrink-0 max-md:pt-[env(safe-area-inset-top)] max-md:pb-[calc(env(safe-area-inset-bottom)+8px)]`}
      style={{ background: "#0f0b17" }}
    >
      {/* Logo - clickable to home of current space */}
      <button onClick={() => navigate(espaceMode ? "/mon-espace" : "/dashboard")} className={`${collapsed ? "p-3" : "px-5 pt-4 pb-2"} flex items-center justify-center hover:opacity-80 transition-opacity`} data-testid="sidebar-logo-btn">
        <img src={currentSociete?.builder?.branding?.logo_url || sidebarLogo || LOGO_URL} alt="Logo" className={`${collapsed ? "w-14" : "h-20 md:h-32 w-auto max-w-[180px]"} object-contain`} data-testid="sidebar-logo" />
      </button>

      {/* Iter 271/274 — Commutateur Super Admin / Panel Admin / Mon Espace */}
      {showSpaceSwitch && (
        <div className={`${collapsed ? "px-2" : "px-3"} pb-2`}>
          <div className={`flex ${collapsed ? "flex-col" : ""} rounded-lg border border-white/10 overflow-hidden`} style={{ background: "#1a1526" }} data-testid="space-switcher">
            <button
              onClick={() => navigate("/dashboard")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-bold transition-colors ${!espaceMode ? "bg-[#f7c935]/15 text-[#f7c935]" : "text-slate-500 hover:text-white hover:bg-white/5"}`}
              title="Panel Admin"
              data-testid="switch-to-admin"
            >
              <Shield className="w-3.5 h-3.5 flex-shrink-0" /> {!collapsed && <span>Admin</span>}
            </button>
            <button
              onClick={() => navigate("/mon-espace")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[11px] font-bold transition-colors ${espaceMode ? "bg-emerald-500/15 text-emerald-400" : "text-slate-500 hover:text-white hover:bg-white/5"}`}
              title="Mon Espace"
              data-testid="switch-to-espace"
            >
              <CircleUser className="w-3.5 h-3.5 flex-shrink-0" /> {!collapsed && <span>Mon Espace</span>}
            </button>
          </div>
        </div>
      )}

      {/* Société Switcher — masqué dans le panel Super Admin (gestion tenants uniquement) */}
      {societes.length > 0 && !collapsed && (
        <div className="px-3 pb-2">
          <button
            ref={societeSwitcherRef}
            onClick={() => setShowSocieteSwitcher(!showSocieteSwitcher)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border border-white/10 hover:border-white/20 transition-colors"
            style={{ background: "#1a1526" }}
            data-testid="societe-switcher-btn"
          >
            {currentSociete?.logo ? (
              <img src={currentSociete.logo} alt="" className="w-7 h-7 rounded-lg object-contain flex-shrink-0 bg-white/5" data-testid="societe-switcher-logo" />
            ) : (
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: currentSociete?.color || "#10b981" }}>
                <Building2 className="w-3.5 h-3.5 text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0 text-left">
              <div className="text-xs font-semibold text-white truncate">{currentSociete?.name || "Societe"}</div>
              {currentSociete?.forme_juridique && <div className="text-[9px] text-slate-500 truncate">{currentSociete.forme_juridique}</div>}
            </div>
            <ChevronsUpDown className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
          </button>

          <PortalDropdown
            open={showSocieteSwitcher}
            onClose={() => setShowSocieteSwitcher(false)}
            triggerRef={societeSwitcherRef}
            align="left"
          >
            <div data-testid="societe-switcher-dropdown">
              {societes.map(s => (
                <button
                  key={s.societe_id}
                  onClick={() => { switchSociete(s); setShowSocieteSwitcher(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-white/5 transition-colors ${currentSociete?.societe_id === s.societe_id ? "bg-white/5" : ""}`}
                  data-testid={`societe-option-${s.societe_id}`}
                >
                  {s.logo ? (
                    <img src={s.logo} alt="" className="w-6 h-6 rounded object-contain flex-shrink-0 bg-white/5" />
                  ) : (
                    <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ background: s.color || "#10b981" }}>
                      <Building2 className="w-3 h-3 text-white" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-white truncate">{s.name}</div>
                    {s.siren && <div className="text-[9px] text-slate-500">{s.siren}</div>}
                  </div>
                  {currentSociete?.societe_id === s.societe_id && (
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#10b981] flex-shrink-0" />
                  )}
                </button>
              ))}
              <div className="border-t border-white/5">
                {(role === "superadmin" || (tenantModules.includes("comptabilite") && (role === "tenant_admin" || !!(user?.module_permissions || {})["comptabilite.view"]))) && societes.length > 1 && (
                  <button
                    onClick={() => { navigate("/admin/comptabilite/consolide"); setShowSocieteSwitcher(false); onNavigate?.(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[10px] font-bold text-[#f7c935] hover:bg-[#f7c935]/10 transition-colors"
                    data-testid="consolidated-view-btn"
                  >
                    <Network className="w-3 h-3" /> Vue groupe (consolidation)
                  </button>
                )}
                <button
                  onClick={() => { navigate("/admin/societes"); setShowSocieteSwitcher(false); onNavigate?.(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-[10px] text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                  data-testid="manage-societes-btn"
                >
                  <Settings className="w-3 h-3" /> {t("nav.manage_societes")}
                </button>
              </div>
            </div>
          </PortalDropdown>
        </div>
      )}

      {/* Main nav */}
      <nav className="flex-1 px-3 py-2 space-y-0.5">
        {/* Notifications as first nav item */}
        <NavLink
          to="/notifications"
          data-testid="notif-bell"
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive ? "bg-[#f7c935]/10 text-[#f7c935]" : "text-slate-400 hover:text-white hover:bg-white/5"
            }`
          }
        >
          <Bell className="w-[18px] h-[18px] flex-shrink-0" />
          {!collapsed && <span className="flex-1">{t("nav.notifications")}</span>}
          {totalNotifs > 0 && (
            <span className="min-w-[20px] h-5 flex items-center justify-center rounded-full text-[10px] font-bold bg-red-500 text-white px-1">
              {totalNotifs > 99 ? "99+" : totalNotifs}
            </span>
          )}
        </NavLink>
        {!collapsed && groupLabels.length > 0 && (
          <button
            onClick={toggleAllGroups}
            data-testid="toggle-all-groups"
            className="flex items-center justify-end gap-1.5 w-full px-3 pt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-[#f7c935] transition-colors"
          >
            {allGroupsCollapsed ? <ChevronsUpDown className="w-3 h-3 flex-shrink-0" /> : <ChevronsDownUp className="w-3 h-3 flex-shrink-0" />}
            <span>{allGroupsCollapsed ? "Tout déplier" : "Tout replier"}</span>
          </button>
        )}
        {mainGroups.map((group, gi) => (
          <div key={gi}>
            {group.label && !collapsed && (
              <button
                onClick={() => toggleGroup(group.label)}
                className="flex items-center justify-between gap-2 w-full text-[10px] font-bold uppercase tracking-wider px-3 pt-4 pb-1.5 hover:opacity-80 transition-opacity"
                style={{ color: group.isBlitzz ? "#f7c935" : "#a899b8" }}
                data-testid={`nav-group-${group.label.replace(/[^a-z]/gi, "-").toLowerCase()}`}
              >
                <span className="flex items-center gap-1.5 min-w-0 whitespace-nowrap truncate">
                  {group.isBlitzz && <Zap className="w-3 h-3 flex-shrink-0" style={{ color: "#f7c935" }} />}
                  {translateLabel(group.label, t)}
                </span>
                <ChevronDown
                  className={`w-3 h-3 flex-shrink-0 transition-transform duration-200 ${collapsedGroups[group.label] ? "-rotate-90" : ""}`}
                />
              </button>
            )}
            {group.label && collapsed && <div className="border-t border-white/5 mx-2 my-2" />}
            {(!group.label || !collapsedGroups[group.label]) && (
              <div className={`${group.label && !collapsed ? "overflow-hidden transition-all duration-200" : ""}`}>
                {group.links.map((link) => <NavItem key={link.to} link={link} />)}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* User + bottom links */}
      <div className="p-4 border-t border-white/[0.06]">
        {/* Bottom links (Equipe, Ma Fiche, Parametres) */}
        {bottomLinks.length > 0 && (
          <div className="mb-3 space-y-0.5">
            {bottomLinks.map(link => <NavItem key={link.to} link={link} />)}
          </div>
        )}

        {/* User info - clickable to expand profile menu */}
        <div className="relative">
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex items-center gap-3 w-full mb-1 px-2 py-2 rounded-lg hover:bg-white/[0.04] transition-colors"
            data-testid="profile-menu-btn"
          >
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold"
                style={{ background: "#461d8a", color: ROLE_COLORS[role] || "#f7c935" }}>
                {user?.name?.[0] || "U"}
              </div>
            )}
            {!collapsed && (
              <>
                <div className="min-w-0 flex-1 text-left">
                  <div className="text-sm font-semibold text-white truncate">{user?.name || "Utilisateur"}</div>
                  <div className="text-[10px] font-medium truncate" style={{ color: ROLE_COLORS[role] || "#f7c935" }}>{ROLE_LABELS[role] || role}</div>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-200 ${showProfileMenu ? "rotate-180" : ""}`} />
              </>
            )}
          </button>

          {/* Profile dropdown */}
          {showProfileMenu && !collapsed && (
            <div className="mb-2 mx-1 rounded-lg border border-white/10 overflow-hidden" style={{ background: "#1a1526" }} data-testid="profile-dropdown">
              <NavLink
                to="/staff/my-profile"
                onClick={() => { setShowProfileMenu(false); onNavigate?.(); }}
                className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                data-testid="profile-link-fiche"
              >
                <UserCircle className="w-4 h-4" />
                <span>{t("nav.ma_fiche")}</span>
              </NavLink>
              <button
                onClick={() => { setShowProfileMenu(false); setShowPwDialog(true); setPwMsg({ type: "", text: "" }); setPwForm({ current: "", new: "", confirm: "" }); }}
                className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                data-testid="profile-link-password"
              >
                <KeyRound className="w-4 h-4" />
                <span>{t("nav.change_password")}</span>
              </button>
              <NavLink to="/securite" onClick={() => setShowProfileMenu(false)} className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm text-slate-400 hover:text-white hover:bg-white/5 transition-colors" data-testid="profile-link-2fa">
                <ShieldCheck className="w-4 h-4" />
                <span>Double authentification</span>
              </NavLink>
            </div>
          )}
        </div>

        {/* Language + Logout */}
        <div className={"flex items-center " + (collapsed ? "flex-col gap-1" : "justify-between gap-1")}>
          {!collapsed && <LanguageSwitcher compact />}
          <button data-testid="logout-btn" onClick={handleLogout}
            className={"flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:text-red-400 hover:bg-red-400/10 transition-colors " + (collapsed ? "w-full" : "flex-1 justify-start")}>
            <LogOut className="w-4 h-4" />
            {!collapsed && <span>{t("common.logout")}</span>}
          </button>
        </div>

        {/* Theme toggle */}
        <ThemeToggle collapsed={collapsed} t={t} />
      </div>

      <button onClick={() => setCollapsed(!collapsed)}
        className="p-2 mx-3 mb-3 rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-colors">
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      {/* Password change dialog */}
      {showPwDialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={() => setShowPwDialog(false)}>
          <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 w-full max-w-sm mx-4" onClick={e => e.stopPropagation()} data-testid="password-dialog">
            <h3 className="text-lg font-bold mb-4" style={{ color: "#f7c935" }}>{t("password.title")}</h3>
            {pwMsg.text && (
              <div className={`mb-4 p-3 rounded-lg text-sm border ${pwMsg.type === "error" ? "border-red-500/30 bg-red-500/10 text-red-400" : "border-green-500/30 bg-green-500/10 text-green-400"}`} data-testid="pw-message">
                {pwMsg.type === "success" && <CheckCircle2 className="w-4 h-4 inline mr-1" />}
                {pwMsg.text}
              </div>
            )}
            <div className="space-y-3">
              {[
                { key: "current", label: t("password.current"), placeholder: t("password.placeholder_current") },
                { key: "new", label: t("password.new"), placeholder: t("password.placeholder_new") },
                { key: "confirm", label: t("password.confirm"), placeholder: t("password.placeholder_confirm") },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs text-slate-500 mb-1 block">{f.label}</label>
                  <div className="relative">
                    <input
                      type={pwShow[f.key] ? "text" : "password"}
                      value={pwForm[f.key]}
                      onChange={e => setPwForm({ ...pwForm, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2.5 pr-10 text-sm focus:border-[#f7c935] focus:outline-none"
                      data-testid={`pw-${f.key}`}
                    />
                    <button type="button" onClick={() => setPwShow({ ...pwShow, [f.key]: !pwShow[f.key] })} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white" data-testid={`pw-toggle-${f.key}`}>
                      {pwShow[f.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowPwDialog(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-white/10 text-sm text-slate-400 hover:bg-white/5 transition-colors">{t("password.cancel")}</button>
              <button onClick={handleChangePassword} disabled={pwLoading} className="flex-1 px-4 py-2.5 rounded-lg text-sm font-bold transition-colors bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] disabled:opacity-50" data-testid="pw-submit">
                {pwLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("password.submit")}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

function ThemeToggle({ collapsed, t }) {
  const { theme, toggle } = useTheme();
  const tx = t || ((s) => s);
  return (
    <button data-testid="theme-toggle" onClick={toggle}
      className="flex items-center gap-2 w-full px-3 py-2 mt-1 rounded-lg text-sm text-slate-400 hover:text-[#f7c935] hover:bg-[#f7c935]/10 transition-colors">
      {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      {!collapsed && <span>{theme === "dark" ? tx("nav.light_mode") : tx("nav.dark_mode")}</span>}
    </button>
  );
}
